/**
 * usePlatformCapability — Triveny-side capability gating.
 *
 * Fetches the current user's aggregated capabilities from
 *   GET /api/platform/roles/me
 * and exposes a sync-feeling check.
 *
 * Returns:
 *   • capabilities  — Set<string> of all granted capability keys
 *   • roleKeys      — string[] of the user's assigned role_keys
 *   • primaryRoleKey— string (used for label / fallback gates)
 *   • can(key)      — boolean (false until first fetch resolves)
 *   • isLoading     — true until first fetch
 *
 * Cached in module scope so multiple components share one fetch per page.
 * Call refresh() to invalidate (e.g. after a role change).
 */

import { useCallback, useEffect, useState } from 'react';
import { config } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface MeResponse {
  user_id: string;
  tenant_id: string;
  role_keys: string[];
  primary_role_key: string;
  capabilities: string[];
}

let _cache: MeResponse | null = null;
let _inflight: Promise<MeResponse | null> | null = null;
let _version = 0;
const _listeners = new Set<() => void>();

async function fetchMe(): Promise<MeResponse | null> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await authFetch(`${config.weaveinApi.baseUrl}/api/platform/roles/me`);
      if (!res.ok) return null;
      const data = (await res.json()) as MeResponse;
      _cache = data;
      return data;
    } catch {
      return null;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

function refresh() {
  _cache = null;
  _inflight = null;
  _version++;
  _listeners.forEach((cb) => cb());
}

export function usePlatformCapability() {
  const [, force] = useState(0);
  const [data, setData] = useState<MeResponse | null>(_cache);
  const [isLoading, setLoading] = useState(!_cache);

  useEffect(() => {
    // Skip entirely on WeaveIn — the platform RBAC endpoint won't be there.
    if (!config.isTriveny) {
      setLoading(false);
      return;
    }
    let alive = true;
    fetchMe().then((d) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    const onChange = () => force((v) => v + 1);
    _listeners.add(onChange);
    return () => {
      alive = false;
      _listeners.delete(onChange);
    };
  }, [_version]);

  const can = useCallback(
    (capability: string): boolean => {
      if (!config.isTriveny) return true; // WeaveIn pages aren't gated by this hook
      if (!data) return false;
      return data.capabilities.includes(capability);
    },
    [data],
  );

  return {
    capabilities: new Set(data?.capabilities ?? []),
    roleKeys: data?.role_keys ?? [],
    primaryRoleKey: data?.primary_role_key ?? '',
    can,
    isLoading,
    refresh,
  };
}
